<template>
  <div>
    <div class="row wrapper border-bottom white-bg page-heading">
      <div class="col-lg-10">
        <h2>RBQM Baseline Historical Dashboard</h2>
        <small>Leveraging Historical Data for Risk-Based Quality Management</small>
      </div>
      <div class="col-lg-2 align-self-center" v-if="showCloseButton == true">
        <i class="fa fa-times close" @click="$router.go(-1)"></i>
      </div>
    </div>

    <div class="py-2 rbqm-dashboard">
      <!-- Controls Section -->
      <div class="card mb-2 shadow-sm border-0">
        <div class="card-body d-flex justify-content-between gap-1 py-1">
         <div class="control-group form-group col-md-4 mb-1">
            <label for="therapeutic-area" class="form-label mb-0">
              Therapeutic Area / Condition
            </label>

            <div class="search-input-wrapper">
              <input
                id="therapeutic-area"
                class="form-control therapeutic-input"
                type="text"
                v-model="therapeuticArea"
                @input="autofillFun"
                @focus="showSuggestions = true"
                @blur="handleBlur"
              />

              <!-- Right-side search icon (no button) -->
              <i class="fa fa-search search-icon" @click="updateDashboard"></i>

              <!-- Custom dropdown suggestions -->
              <ul
                v-if="showSuggestions && autofilldata && autofilldata.length"
                class="suggestions-dropdown list-group"
              >
                <li
                  v-for="value in autofilldata"
                  :key="value"
                  class="list-group-item suggestion-item"
                  @mousedown.prevent="selectSuggestion(value)"
                >
                  {{ value }}
                </li>
              </ul>
            </div>
          </div>


          <div class="control-group form-group col-md-4 mb-1">
            <label for="study-phase" class="form-label mb-0">Study Phase</label>
            <select
              id="study-phase"
              class="form-select"
              v-model="studyPhase"
              @change="updateDashboard"
            >
              <option value="PHASE1">Phase 1</option>
              <option value="PHASE2">Phase 2</option>
              <option value="PHASE3">Phase 3</option>
              <option value="PHASE4">Phase 4</option>
            </select>
          </div>

          <div class="control-group form-group col-md-4 mb-1">
            <label for="time-range" class="form-label mb-0">Time Range</label>
            <select
              id="time-range"
              class="form-select"
              v-model="timeRange"
              @change="updateDashboard"
            >
              <option value="1">Last 1 Year</option>
              <option value="2">Last 2 Years</option>
              <option value="5">Last 5 Years</option>
              <option value="7">Last 7 Years</option>
              <option value="10">Last 10 Years</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
      </div>

      <!-- KPI Cards -->
      <div class="row g-2 mb-2 kpi-container">
        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Total Study</p>
              <h3 class="mb-1">{{ totalstudycount }}</h3>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Completed Study</p>
              <h3 class="mb-1">{{ completedstudycount }}</h3>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Terminated Study</p>
              <h3 class="mb-1">{{ terminatedstudycount }}</h3>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Withdrawn Study</p>
              <h3 class="mb-1">{{ withdrawnstudycount }}</h3>
            </div>
          </div>
        </div>
      </div>
  <div class="row g-2 mb-2 kpi-container">
        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Avg Planned Enrollment</p>
              <h3 class="mb-1">{{ avgEnrollment }}</h3>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Avg Study Duration (Months)</p>
              <h3 class="mb-1">{{ avgDuration }}</h3>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-0 text-muted">Study Success Rate</p>
              <h3 class="mb-1">{{ completionRate }}%</h3>
            </div>
          </div>
        </div>

        <div class="col-12 col-md-3">
          <div class="card kpi-card">
            <div class="card-body p-0 d-flex justify-content-between align-items-center">
              <p class="mb-2 text-muted">Avg Risk Score</p>
              <h3 class="mb-1">{{ riskScore }}</h3>
            </div>
          </div>
        </div>
      </div>
      <!-- Charts Row 1 -->
      <div class="row g-2 mb-2">
        <!-- Heatmap with loader + pagination -->
        <div class="col-12 col-lg-6">
          <div class="card chart-container h-100">
            <div class="card-body py-2">
              <h4 class="mt-2">Risk Heat Map by Therapeutic Area</h4>

              <!-- Loader -->
              <div
                v-if="loadingHeatmap"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Chart + pagination -->
              <template v-else>
                <apexchart
                  type="heatmap"
                  height="300"
                  :options="riskHeatmapOptions"
                  :series="riskHeatmapSeries"
                />

                <div
                  v-if="heatmapTotalPages > 1"
                  class="d-flex justify-content-between align-items-center mt-0"
                >
                  <button
                    class="btn btn-sm btn-outline-secondary py-0"
                    :disabled="heatmapCurrentPage === 1"
                    @click="prevHeatmapPage"
                  >
                    Previous
                  </button>

                  <span>Page {{ heatmapCurrentPage }} of {{ heatmapTotalPages }}</span>

                  <button
                    class="btn btn-sm btn-outline-secondary py-0"
                    :disabled="heatmapCurrentPage === heatmapTotalPages"
                    @click="nextHeatmapPage"
                  >
                    Next
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Protocol Complexity Risk Analysis -->
        <div class="col-12 col-lg-6">
          <div class="card chart-container h-100">
            <div class="card-body py-2">
              <h4 class="mt-2">Protocol Complexity Risk Analysis</h4>

              <!-- Loader -->
              <div
                v-if="loadingComplexity"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Chart / empty state -->
              <apexchart
                v-else-if="complexitySeries && complexitySeries.length"
                type="donut"
                height="340"
                :options="complexityOptions"
                :series="complexitySeries"
              />
              <p v-else class="text-muted mb-0">
                No complexity data available for the selected filters.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row 2 -->
      <div class="row g-2 mb-2">
        <!-- Site Performance Risk Indicators -->
        <div class="col-12 col-lg-12">
          <div class="card chart-container h-100">
            <div class="card-body py-2">
              <h4 class="mt-2">Site Performance Risk Indicators</h4>

              <!-- Loader -->
              <div
                v-if="loadingSitePerformance"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Chart -->
              <apexchart
                v-else
                type="bar"
                height="300"
                :options="sitePerformanceOptions"
                :series="sitePerformanceSeries"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- QTL Distribution -->
      <!-- <div class="row g-2 mb-2">
        <div class="col-12">
          <div class="card chart-container">
            <div class="card-body">
              <h3 class="h5 mb-3">
                Quality Tolerance Limits - Historical Distribution
              </h3>

              <div
                v-if="loadingQTL"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <apexchart
                v-else
                type="area"
                height="400"
                :options="qtlOptions"
                :series="qtlSeries"
              />
            </div>
          </div>
        </div>
      </div> -->

      <!-- Recent Trials Table -->
      <div class="row g-2 mb-2">
        <div class="col-12">
          <div class="card chart-container">
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <h4 class="mt-2">Recent Trials with High Risk Indicators</h4>

              
              </div>

              <!-- Loader when loadhighriskindiator = true -->
              <div
                v-if="loadhighriskindiator"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Table when loading is false -->
              <div
                v-else
                id="high-risk-trials"
                class="table-responsive"
              >
                <table
                  class="table table-striped table-hover align-middle trials-table mb-0"
                >
                  <thead class="table-light">
                    <tr>
                      <th>Trial ID</th>
                      <th>Therapeutic Area</th>
                      <th>Phase</th>
                      <th>Risk Factors</th>
                      <th>Risk Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="trial in highRiskTrials"
                      :key="trial.id"
                      class="trial-row"
                    >
                      <td>{{ trial.trial_id }}</td>
                      <td>{{ trial.therapeutic_area_display }}</td>
                      <td>{{ trial.phase }}</td>
                      <td>{{ trial.risk_factors }}</td>
                      <td>
                        <span
                          class="risk-indicator"
                          :class="riskClass(trial.risk_level)"
                        >
                          {{ trial.risk_score }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="./rbqmInteligentDashboard.js"></script>

<style scoped>

.rbqm-dashboard {
  --primary-color: #2c3e50;
  --secondary-color: #3498db;
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --danger-color: #e74c3c;
  --light-gray: #ecf0f1;
  --dark-gray: #7f8c8d;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.header {
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  color: white;
  border-radius: 0.75rem;
}


.control-group {
  display: flex;
  flex-direction: column;
  min-width: 220px;
}

/* KPI cards */
.kpi-card {
  border-left: 4px solid var(--secondary-color);
  border-radius: 0.75rem;
}

.kpi-card h3 {
  color: var(--secondary-color);
  font-size: 22px;
  font-weight: 600;
}

.kpi-card p {
  color: var(--dark-gray);
  font-size: 12px;
}

/* Charts */
.chart-container {
  border-radius: 0.75rem;
}

/* Risk indicators */
.risk-indicator {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.risk-low {
  background: var(--success-color);
  color: white;
}

.risk-medium {
  background: var(--warning-color);
  color: white;
}

.risk-high {
  background: var(--danger-color);
  color: white;
}

/* Table */
.trials-table th,
.trials-table td {
  padding-top: 0.50rem;
  padding-bottom: 0.50rem;
  line-height: .5;
}
.search-input-wrapper {
  position: relative;
}
.suggestions-dropdown {
  position: absolute;
  top: 100%;           /* exactly below the input */
  left: 0;
  right: 0;
  z-index: 1050;
  max-height: 220px;
  overflow-y: auto;
  margin-top: 2px;
}
.suggestion-item {
  cursor: pointer;
  font-size: 0.9rem;
}

.suggestion-item:hover {
  background-color: #f1f1f1;
}
/* The text box */
.therapeutic-input {
  padding-right: 2rem; /* space for the search icon on the right */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Hide the datalist dropdown arrow in WebKit browsers (Chrome/Edge) */
.therapeutic-input::-webkit-calendar-picker-indicator {
  display: none !important;
}

/* Search icon inside the input on the right */
.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  /* pointer-events: none;  <-- remove this line */
  cursor: pointer;          /* optional: show hand cursor */
  font-size: 0.9rem;
}
.chart-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.chart-scroll-inner {
  /* Height is controlled by the chart itself. */
  min-height: 400px;
}
</style>
